/**
 * SchedulingLinksManager
 *
 * Dialog for managing scheduling links (Calendly-style).
 * Lists links with create/edit/delete/toggle/copy URL/view bookings actions.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, Copy, Calendar, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type { SchedulingLinkDetail, SchedulingLinkInput, BookingDetail } from '@/lib/api';
import type { CalendarCollection, TimeFormat } from '../types';
import { SchedulingLinkForm } from './SchedulingLinkForm';
import { BookingsList } from './BookingsList';
import { shareOrigin } from '@/lib/portal-host';

interface SchedulingLinksManagerProps {
  open: boolean;
  onClose: () => void;
  schedulingLinks: SchedulingLinkDetail[];
  calendars: CalendarCollection[];
  onCreateLink: (input: SchedulingLinkInput) => Promise<SchedulingLinkDetail>;
  onUpdateLink: (id: string, input: Partial<SchedulingLinkInput>) => Promise<SchedulingLinkDetail>;
  onDeleteLink: (id: string) => Promise<void>;
  onFetchBookings: (linkId: string, status?: string) => Promise<BookingDetail[]>;
  onCancelBooking: (linkId: string, bookingId: string) => Promise<void>;
  timeFormat?: TimeFormat;
  defaultAvailabilityWindows?: { day: number; startHour: number; endHour: number }[];
  defaultBookingConfirmationMessage?: string;
}

type Mode = 'list' | 'create' | 'edit' | 'bookings';

export function SchedulingLinksManager({
  open,
  onClose,
  schedulingLinks,
  calendars,
  onCreateLink,
  onUpdateLink,
  onDeleteLink,
  onFetchBookings,
  onCancelBooking,
  timeFormat,
  defaultAvailabilityWindows,
  defaultBookingConfirmationMessage,
}: SchedulingLinksManagerProps) {
  const [mode, setMode] = useState<Mode>('list');
  const [editingLink, setEditingLink] = useState<SchedulingLinkDetail | null>(null);
  const [viewingBookingsLink, setViewingBookingsLink] = useState<SchedulingLinkDetail | null>(null);
  const [bookings, setBookings] = useState<BookingDetail[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('list');
      setEditingLink(null);
      setViewingBookingsLink(null);
    }
  }, [open]);

  const handleCopyUrl = (slug: string) => {
    const url = `${shareOrigin()}/a/arche_calendar/book/${slug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copied to clipboard' });
  };

  const handleToggleActive = async (link: SchedulingLinkDetail) => {
    try {
      await onUpdateLink(link.id, { isActive: !link.isActive });
      toast({ title: link.isActive ? 'Link deactivated' : 'Link activated' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update link',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onDeleteLink(id);
      toast({ title: 'Scheduling link deleted' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to delete link',
        variant: 'destructive',
      });
    }
  };

  const handleViewBookings = async (link: SchedulingLinkDetail) => {
    setViewingBookingsLink(link);
    setMode('bookings');
    setBookingsLoading(true);
    try {
      const result = await onFetchBookings(link.id);
      setBookings(result);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to load bookings',
        variant: 'destructive',
      });
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!viewingBookingsLink) return;
    try {
      await onCancelBooking(viewingBookingsLink.id, bookingId);
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: 'cancelled' as const } : b)),
      );
      toast({ title: 'Booking cancelled' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to cancel booking',
        variant: 'destructive',
      });
    }
  };

  const handleSaveLink = async (input: SchedulingLinkInput) => {
    if (mode === 'edit' && editingLink) {
      await onUpdateLink(editingLink.id, input);
      toast({ title: 'Scheduling link updated' });
    } else {
      await onCreateLink(input);
      toast({ title: 'Scheduling link created' });
    }
    setMode('list');
    setEditingLink(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'list'
              ? 'Scheduling Links'
              : mode === 'create'
                ? 'New Scheduling Link'
                : mode === 'edit'
                  ? 'Edit Scheduling Link'
                  : `Bookings: ${viewingBookingsLink?.title}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'list'
              ? 'Create bookable links that others can use to schedule time with you.'
              : mode === 'bookings'
                ? 'View and manage bookings for this scheduling link.'
                : 'Configure your scheduling link details and availability.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {mode === 'list' && (
            <>
              <div className="space-y-2">
                {schedulingLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center gap-2 border rounded-md px-3 py-2"
                  >
                    {link.color && (
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: link.color }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {link.title}
                        {!link.isActive && (
                          <span className="text-xs text-muted-foreground ml-1.5">(inactive)</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        /{link.slug} &middot; {link.durationMinutes}min
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleCopyUrl(link.slug)}
                      title="Copy public URL"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleToggleActive(link)}
                      title={link.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {link.isActive ? (
                        <Eye className="h-3.5 w-3.5" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleViewBookings(link)}
                      title="View bookings"
                    >
                      <Calendar className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingLink(link);
                        setMode('edit');
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => handleDelete(link.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {schedulingLinks.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No scheduling links yet. Create one to let others book time with you.
                  </p>
                )}
              </div>
              <Button onClick={() => setMode('create')} className="w-full">
                <Plus className="h-4 w-4 mr-1" />
                New Scheduling Link
              </Button>
            </>
          )}

          {(mode === 'create' || mode === 'edit') && (
            <SchedulingLinkForm
              initialData={editingLink}
              calendars={calendars}
              onSave={handleSaveLink}
              onBack={() => {
                setMode('list');
                setEditingLink(null);
              }}
              defaultAvailabilityWindows={defaultAvailabilityWindows}
              defaultBookingConfirmationMessage={defaultBookingConfirmationMessage}
            />
          )}

          {mode === 'bookings' && (
            <>
              {bookingsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <BookingsList
                  bookings={bookings}
                  onCancelBooking={handleCancelBooking}
                  timeFormat={timeFormat}
                />
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setMode('list');
                  setViewingBookingsLink(null);
                }}
              >
                Back
              </Button>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
